import type { BrandingTheme } from "@tdelivr/api-client/generated/tenant/tenant-branding.js";
import {
  platformInk,
  resolveBrand,
  type ResolvedRole,
  type StateClassKey,
} from "@tdelivr/design-tokens";

/**
 * What the brand board does to the themes TEN-04 returns, away from any rendering.
 *
 * The same split as `zones.ts` and `tenant-audit.ts`: searching, ordering, paging and deciding what
 * a colour's state actually is are all stateable and testable without a browser.
 *
 * **The finding this screen exists to record is that nothing on this platform checks a brand
 * colour, and the page the design draws says in its own sentence that something does.** TEN-04's
 * write validates three things — a six-digit hex, an HTTPS asset URL with no credentials, and a
 * display name between 1 and 120 characters — and `nextTheme()` validates only the version. There
 * is no contrast check and no danger-collision check in the route, the service or the domain. A
 * tenant can publish a primary no text can sit on, and a primary indistinguishable from the
 * platform's danger red, and TEN-04 will answer 200. R-16 in
 * `docs/design/DESIGN_RECONCILIATION.md` is the measurement.
 *
 * **The check is therefore done here, and not invented here.** `@tdelivr/design-tokens` already
 * implements it — `resolveBrand`, which is the function `01 Token System v2.dc.html`'s own brand
 * contract describes and which the two mobile applications render through. So this module resolves
 * and reports; it does not decide what safe means.
 */

/** A resolved role, its name, and the theme it belongs to. */
export interface BrandRow {
  readonly id: string;
  /** The design's `Item`: which colour, and of which theme when there is more than one. */
  readonly item: string;
  /** The design's `Value`: the hex exactly as TEN-04 published it, before any fallback. */
  readonly value: string;
  readonly role: "primary" | "secondary" | "accent";
  readonly resolved: ResolvedRole;
  /** True when the published value was not a hex colour and the platform's own was substituted. */
  readonly malformed: boolean;
  /** True when this colour is close enough to the platform's danger red to be mistaken for it. */
  readonly nearDanger: boolean;
  /** The warnings the resolver raised about this role, in its own words. */
  readonly warnings: readonly string[];
  readonly theme: BrandingTheme;
}

/**
 * The three colours TEN-04 publishes per theme, in the contract's own order.
 *
 * `secondary` is in the list and is not in the design's two sample rows, and that is the contract's
 * doing rather than an addition here: `BrandingTheme.colors` requires all three and the write
 * refuses a body missing any of them, so a board drawing two of three would hide one an
 * administrator may not know they have. The design's rows are sample data, not a column list.
 */
export const COLOUR_ROLES: readonly BrandRow["role"][] = ["primary", "secondary", "accent"];

/** Every theme's colours as grid rows, resolved by the platform's own brand resolver. */
export function brandRows(themes: readonly BrandingTheme[]): BrandRow[] {
  const many = themes.length > 1;
  const rows: BrandRow[] = [];
  for (const theme of themes) {
    const resolution = resolveBrand(theme.colors);
    for (const role of COLOUR_ROLES) {
      const warnings = resolution.warnings.filter((warning) => warning.role === role);
      const label = `${role.charAt(0).toUpperCase()}${role.slice(1)} colour`;
      rows.push({
        id: `${theme.key}:${role}`,
        // With one theme this is the design's own text. With more than one it is prefixed, because
        // "Primary colour" appearing three times with three different hexes names nothing.
        item: many ? `${theme.displayName} · ${label}` : label,
        value: theme.colors[role],
        role,
        resolved: resolution[role],
        malformed: warnings.some((warning) => warning.code === "malformed_colour"),
        nearDanger: warnings.some((warning) => warning.code.endsWith("_near_danger")),
        warnings: warnings.map((warning) => warning.message),
        theme,
      });
    }
  }
  return rows;
}

/** The three outcomes the resolver can reach for one role. */
export type ColourVerdict = "safe" | "fill_only" | "replaced";

export function verdictOf(row: BrandRow): ColourVerdict {
  if (row.malformed) return "replaced";
  return row.resolved.safeForText ? "safe" : "fill_only";
}

/**
 * The design's `Checked` cell.
 *
 * The design's two sample cells are `AA on white 10.4:1` and `fails as text · used as dot only`,
 * and both are the resolver's own two outcomes in the resolver's own grammar: the first names the
 * foreground it chose and the ratio it reached, and the second is `safeForText: false`, whose
 * documented consequence in `01 Token System v2.dc.html`'s contract table is "That role carries no
 * text. Buttons switch to ink with a brand dot". So the words are kept and the numbers are
 * computed.
 *
 * `white` and `ink` rather than the hexes, because the design writes `white`. The platform's ink is
 * `#1f2933` and not black, which `contrast.ts` explains at length is the whole reason a failure is
 * possible at all.
 */
export function checkedText(row: BrandRow): string {
  if (row.malformed) return "not a hex colour · platform colour used";
  const ground = row.resolved.foreground === platformInk.paper ? "white" : "ink";
  if (!row.resolved.safeForText) {
    return `fails as text · used as dot only · best is ${ratioText(row.resolved.contrast)} on ${ground}`;
  }
  return `AA on ${ground} ${ratioText(row.resolved.contrast)}`;
}

/** A ratio in the design's own grammar: one decimal place and a `:1`. */
export function ratioText(ratio: number): string {
  return `${ratio.toFixed(1)}:1`;
}

/**
 * The state class and the word.
 *
 * The design's own words for its two sample rows are `live` and `auto-adjusted`, with classes
 * `done` and `caution`, and both are kept for the two outcomes they describe. The third — a value
 * TEN-04 accepted that is not a hex colour, so the platform's own colour is rendered in its place —
 * has no sample row and takes `failed`: not a qualified outcome but a replaced one, because what
 * the tenant chose is not on screen anywhere.
 */
export function colourState(row: BrandRow): { classKey: StateClassKey; label: string } {
  switch (verdictOf(row)) {
    case "safe":
      return { classKey: "done", label: "live" };
    case "fill_only":
      return { classKey: "caution", label: "auto-adjusted" };
    case "replaced":
      return { classKey: "failed", label: "replaced" };
  }
}

/**
 * Why the badge says what it says, and what the platform did not do.
 *
 * On the badge's `title` rather than in a column of its own — the same call `zones.ts` makes, for
 * the same reason: a column the design does not draw is design content invented here.
 *
 * Every row carries one, because on this page one clause is true of every row: the verdict was
 * reached in this browser, a moment ago, and TEN-04 did not reach it when the colour was published.
 * The resolver's own warnings are appended where it raised any, in its words rather than reworded.
 */
export function colourReason(row: BrandRow): string {
  const parts: string[] = [];
  if (row.malformed) {
    parts.push(
      `TEN-04 published "${row.value}" for this role and it is not a hex colour, so the platform's own ${row.resolved.background} is rendered instead.`,
    );
  } else {
    parts.push(
      `Resolved by @tdelivr/design-tokens: ${row.resolved.foreground} reaches ${ratioText(row.resolved.contrast)} on it, which is the best of the platform's ink and paper.`,
    );
  }
  parts.push(...row.warnings);
  parts.push(
    "Checked in this console, now. TEN-04 validates the hex pattern and the asset URLs and checks neither contrast nor the danger collision, so a colour that fails here was published without objection.",
  );
  return parts.join(" ");
}

/** The cap the branding repository applies, read out of its own query rather than assumed. */
export const THEME_CAP = 200;

/**
 * What the brand board owes an administrator about what it fetched.
 *
 * **One sentence, on every render, because the thing it says is always true and can never be
 * detected.** `GET /v1/themes` publishes **no query parameters and no cursor** — the whole of its
 * contract is a 200 carrying `themes` — and the repository behind it runs
 *
 * ```sql
 * SELECT DISTINCT ON (theme_key) * FROM branding_themes
 *  WHERE tenant_id=$1 ORDER BY theme_key, version DESC LIMIT 200
 * ```
 *
 * So the answer is capped at 200 theme keys, alphabetically by key, and **nothing in the response
 * says the cap was reached**: no total, no cursor, no echo of a limit. A tenant with more keys than
 * that is shown the alphabetically-first 200 and the board has no way to know it. That is a
 * different wall from the audit board's — AUD-01 at least publishes the `limit` the caller asked
 * for, so a page ending exactly at it is suspicious — and the same conclusion: say it rather than
 * imply completeness. R-16.
 *
 * The same line carries the other half of this page's absence, because the page's own title is the
 * half that is missing: the design's grid draws two custom-domain rows, and nothing on any of the
 * 142 published contracts carries a tenant's domain, its DNS verification or its certificate. A
 * page called "Brand and domains" showing no domain and saying nothing would read as broken, which
 * is the one thing worse than saying so. One added line rather than two, which is the precedent the
 * audit board set.
 *
 * @param themes how many themes came back, or `undefined` before anything has.
 */
export function fetchNote(themes: number | undefined): string | undefined {
  if (themes === undefined) return undefined;
  const subject = themes === 1 ? "The one theme" : `All ${String(themes)} themes`;
  return `${subject} this tenant's branding service answered, each at its current version. TEN-04's listing publishes no limit and no cursor and the service caps it at ${String(THEME_CAP)} theme keys, so this board cannot tell a complete answer from a truncated one. No domains: nothing on the platform publishes a tenant's custom domain, its DNS state or its certificate.`;
}

/** The sort options the design's toolbar offers, in its order and its words. */
export type BrandOrder = "default" | "state" | "az" | "za";

export interface BrandQuery {
  readonly search: string;
  readonly order: BrandOrder;
  /** 1-based, as the pager shows it. */
  readonly page: number;
  readonly perPage: number;
  /** The verdict chosen in the toolbar's filter, or `undefined` for all of them. */
  readonly verdict?: ColourVerdict;
}

export interface BrandPage {
  readonly rows: readonly BrandRow[];
  /** Every row that matched, across all pages — what an export writes. */
  readonly all: readonly BrandRow[];
  readonly total: number;
  readonly pageCount: number;
}

/** Every word on a row a search should match: the published values and the words on screen. */
function searchableText(row: BrandRow): string {
  return [
    row.item,
    row.value,
    checkedText(row),
    colourState(row).label,
    row.theme.key,
    row.theme.displayName,
    `v${String(row.theme.version)}`,
  ]
    .join(" ")
    .toLowerCase();
}

/**
 * Filter, order and page what the screen holds.
 *
 * `default` is the order TEN-04 answered in — `ORDER BY theme_key, version DESC`, so alphabetically
 * by key — with the contract's own colour order inside each theme. Unlike the zones board there is
 * no precedence to preserve, so re-sorting costs nothing; the default is still the service's,
 * because a board that silently re-sorts has told the reader something about the service that is
 * not true.
 *
 * The filter is over the **verdict**, which is computed here, because TEN-04's listing takes no
 * query parameter at all: there is nothing to refetch with. That is the opposite of the zones
 * board, where choosing a lifecycle refetches, and the difference is the contract's rather than a
 * preference.
 */
export function brandPage(rows: readonly BrandRow[], query: BrandQuery): BrandPage {
  const needle = query.search.trim().toLowerCase();
  const matched = rows.filter(
    (row) =>
      (query.verdict === undefined || verdictOf(row) === query.verdict) &&
      (needle.length === 0 || searchableText(row).includes(needle)),
  );

  const ordered =
    query.order === "default"
      ? matched
      : [...matched].sort((left, right) => {
          if (query.order === "state") {
            return colourState(left).label.localeCompare(colourState(right).label);
          }
          // "Name" is the first column, as the design's A–Z is: the item.
          const compared = left.item.localeCompare(right.item);
          return query.order === "az" ? compared : -compared;
        });

  const total = ordered.length;
  const pageCount = Math.max(1, Math.ceil(total / query.perPage));
  const page = Math.min(Math.max(1, query.page), pageCount);
  const from = (page - 1) * query.perPage;

  return { rows: ordered.slice(from, from + query.perPage), all: ordered, total, pageCount };
}
